import moment from 'moment';
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { __ } from '../../translations';
import Loader from '../Loader';
import { MixpanelContext } from '../../contexts/MixpanelContext';
import { EVENT, KEYS } from '../../contexts/MixpanelConstants';
import { COMPONENT_TYPES, defaultDataStream, dataStreamTypes } from '../../config/constants';
import url from '../../helpers/url';
import h from '../../helpers/common';
import f from '../../helpers/formatter';
import f2 from '../../helpers/formatter2';
import apis from '../../helpers/apis';
import { checkShowLockIcon } from '../../helpers/vod';
import { Carousel } from '@intigral/domain/lib/core/entities/ScreenEntities';
import { Badge } from '@intigral/shared';
import { Carousel2, Program } from '@intigral/ui';
import { SectionHeaderWithPagination } from '@intigral/ui/components/SectionHeaderWithPagination';
import { IProgram } from '@intigral/ui/components/Program/ProgramWithoutPoster';
import { getTvodPricingTemplatesApi } from '../../services/serviceWrapper/tvodServiceWrapper';
import LockIcon from '../elements/LockIcon';
import { IStore } from '../../interfaces/StoreTypes';
import { enhanceChannelsWithPPV, getCountryPPVEvents } from '../../utils/user';
import { get } from '@intigral/utils/lib/browser-storage/localStorage';
import { STORE_NAME } from '@intigral/constant';
import { useCountryCode, useConfigurationSections } from '../../hooks/useConfiguration';
import { useTvod } from '../../hooks/useTvod';

interface Meta {
    title?: {
        en?: string;
        ar?: string;
    };
    carouselType?: string;
    is_rented?: boolean;
    empty_msg?: string;
    programType?: string;
    hasMultiview?: string;
    seeAllScreen?: string;
    defaultActive?: string;
    listbtn_target?: string;
    template_params?: {
        menu_type: string;
    };
    data_stream_type?: string;
    subscriptionGuid?: string;
    subscriptionGuids?: string;
    carousel_link_target?: string;
    show_title_on_listview?: boolean;
}
interface Params {
    vodGuid: any;
    filter_params?: string[];
}
interface StreamingCarouselProps {
    data: { [key: string]: any };
    params?: Params;
    id?: string;
    data_stream?: string;
    content?: { entries: any[]; lastWatchedMovieTitle: string } | null;
    meta?: Meta;
    carousel: Carousel;
}
export const StreamingCarousel: React.FC<StreamingCarouselProps> = (props) => {
    const [content, setContent] = useState<any[]>(Object.values(props?.data || {}));
    const [lastWatchedMovieTitle, setLastWatchedMovieTitle] = useState<string>('');
    const [componentData, setComponentData] = useState<any[] | null>(null);
    const [isDataFetched, toggleData] = useState<boolean>(false);
    const screenHistory = useSelector((state: any) => state.screenHistory);
    const guid = url.getQuery('guid') !== null ? url.decodeValue(url.getQuery('guid')) : null;

    const { isTvod } = useTvod(content);
    const config = useConfigurationSections();

    const channelImageId = config?.channelImage?.id || 'colour_264x26';
    const countryCode = useCountryCode();
    // PPV configuration
    const ppvEventConfig = useSelector((state: IStore) => state?.generalConfiguration?.ppvEventConfig || null);
    const operators = config?.operators || null;

    useLayoutEffect(() => {
        if (!isTvod) return;
        // apis.getJawwyService().getTVODService().getPricingTemplates();
        getTvodPricingTemplatesApi();
    }, [content]);

    const getVodGuid = async (): Promise<any> => {
        const currentScreen = Array.isArray(screenHistory) ? screenHistory.pop() : screenHistory;
        const dataStream =
            (typeof currentScreen === 'object' && currentScreen !== null && currentScreen.screen) === 'movie-details'
                ? defaultDataStream.VOD_DETAILS
                : defaultDataStream.TV_SERIES_DETAILS;
        return await apis.getMpxService().vodDetails().get(dataStream, props.params);
    };

    if (
        !content &&
        (guid === null || guid === 'null') &&
        !isDataFetched &&
        props.params &&
        props.data_stream === defaultDataStream.DIVE_MORE_LIKE_THIS
    ) {
        getVodGuid()
            .then((data) => {
                if (data && data.guid) {
                    props.params.vodGuid = data.guid;
                    apis.getDiveService()
                        .moreLikeThis()
                        .get(props.data_stream, props.params)
                        .then((data) => {
                            if (data && data.length) {
                                setComponentData(data);
                                toggleData(true);
                            }
                        })
                        .catch(() => undefined); // Updated empty function
                }
            })
            .catch(() => undefined); // Updated empty function
    }

    useEffect(() => {
        if (componentData) {
            setContent(componentData);
        }

        if (
            'data_stream' in props &&
            props.data_stream === dataStreamTypes.BECAUSE_YOU_WATCHED &&
            props.content != null
        ) {
            setLastWatchedMovieTitle(props.content.lastWatchedMovieTitle);
            setContent(props.content.entries);
        }
    }, [componentData]);

    const sliderElement = useRef(null);
    //const { track = () => undefined } = useContext(MixpanelContext) || (() => undefined); // Updated empty function
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const { track } = useContext(MixpanelContext) || { track: () => {} };
    const componentId = props.id ? props.id : '';
    const language = h.getLang();

    const handleSeeAllLinkClick = (item: Meta) => {
        track(`${h.getScreen()} - ${EVENT.CAROUSEL.SEE_ALL}`, { [KEYS.GENERAL.CAROUSLE_NAME]: item.title.en });
    };

    const pagingId = `component-${Math.random().toString(36).slice(2, 11)}`;
    const paginationClass = `pagination-${pagingId}`;

    const settings = {
        autoplay: false,
        pagination: false,
        dir: language === 'ar' ? 'rtl' : 'ltr',
        breakpoints: {
            560: {
                slidesPerView: 2,
            },
            768: {
                slidesPerView: 3,
            },
            1212: {
                slidesPerView: 4,
            },
            1500: {
                slidesPerView: 5,
            },
        },
        slidesPerView: 1,
        virtual: false,
    };

    // PPV utility functions
    const getPPVEventForChannel = (channel: any) => {
        if (!channel?.isPPV || !ppvEventConfig || !operators) {
            return null;
        }

        const ppvEvents = getCountryPPVEvents(ppvEventConfig, operators, countryCode);
        const channelSubscriptionGuids = channel?.subscriptions?.map((sub: any) => sub.subscriptionGUID) || [];

        // Find the PPV event that matches this channel's subscription
        const ppvEvent = ppvEvents.find((event: any) => channelSubscriptionGuids.includes(event.subscriptionGuid));

        return ppvEvent || null;
    };

    const isPPVEventPurchased = (ppvEvent: any) => {
        if (!ppvEvent) return false;

        const userInfo = get(STORE_NAME.USER);
        const userPpvList = userInfo?.ppvList || [];

        return userPpvList.some((userPpv: any) => userPpv.eventId === ppvEvent.eventId);
    };

    const handleStreamingItemClick = (event: React.MouseEvent, item: any) => {
        // Check if channel is PPV
        if (item?.isPPV) {
            const ppvEvent = getPPVEventForChannel(item);
            const isPurchased = isPPVEventPurchased(ppvEvent);

            if (isPurchased) {
                // Scenario 1: PPV channel is purchased - allow default behavior (go to LiveTV/rewind)
                return true;
            } else if (ppvEvent?.eventId) {
                // Scenario 2: PPV channel not purchased - go to PPV Modal Details
                event.preventDefault();
                const currentLang = h.getLang ? h.getLang() : 'en';
                const origin = window.location.origin;
                const ppvModalUrl = `${origin}/${currentLang}?eventId=${ppvEvent.eventId}`;
                window.location.href = ppvModalUrl;
                return false;
            }
        }

        // Non-PPV channel or fallback - allow default behavior
        return true;
    };

    const getProgramBadge = (percentage: number) => {
        // For Live
        if (percentage > 0 && percentage < 1) return <Badge live>{__('streaming_carousel_live_badge_text')}</Badge>;
        // For Past
        if (percentage >= 1) return <Badge secondary>{__('streaming_carousel_rewind_badge_text')}</Badge>;
        // For Future
        if (percentage <= 0) return <Badge secondary>{__('streaming_carousel_next_badge_text')}</Badge>;

        return null;
    };

    const getPlayUrl = (percentage: number, item) => {
        const listing = item?.listings?.[0] || {};
        // For Live
        if (percentage > 0 && percentage < 1) return url.route('livetv', { channelId: btoa(item?.channelId) });
        //  For Rewind
        if (percentage >= 1)
            return url.route(url.getDetailsPage('live'), {
                listingId: btoa(listing?.listingId),
                date: btoa(item?.startTime),
            });

        if (!percentage) {
            return url.route(url.getDetailsPage('live'), {
                listingId: btoa(listing?.listingId),
                startDate: btoa(item?.startTime),
            });
        }

        return null;
    };

    const filteredContent = useMemo(() => {
        if (!props.carousel?.data?.programOrder) {
            return content;
        }

        const programOrder = props.carousel.data.programOrder;
        let liveLimit = 1,
            rewindLimit = 0,
            futureLimit = 0;

        programOrder.forEach((program) => {
            if (program.type === 'live') liveLimit = program.count ?? 1;
            if (program.type === 'rewind') rewindLimit = program.count ?? 2;
            if (program.type === 'future') futureLimit = program.count ?? 3;
        });

        const rewindPrograms = [];
        const livePrograms = [];
        const futurePrograms = [];

        content.forEach((item) => {
            const element = item.listings[0];
            if (!element) return;

            const startTime = element?.startTime;
            const endTime = element?.endTime;
            const now = Date.now();
            const percentage = startTime && endTime ? (now - startTime) / (endTime - startTime) : 0;

            const newItem = { ...item, percentage };

            if (percentage >= 1 && rewindPrograms.length < rewindLimit) {
                rewindPrograms.push(newItem);
            } else if (percentage > 0 && percentage < 1 && livePrograms.length < liveLimit) {
                livePrograms.push(newItem);
            } else if (percentage <= 0 && futurePrograms.length < futureLimit) {
                futurePrograms.push(newItem);
            }
        });

        return [...rewindPrograms, ...livePrograms, ...futurePrograms];
    }, [content, props.carousel?.data?.programOrder]);

    const enhancedFilteredContent = enhanceChannelsWithPPV(filteredContent);

    return (
        <>
            {content === null && atob(url.getQuery('guid')) !== 'null' && (
                <Loader componentName={COMPONENT_TYPES.STREAMING_CAROUSEL} />
            )}
            {content !== null && enhancedFilteredContent.length > 0 && (
                <div
                    className="container full-width"
                    ref={sliderElement}
                    id={componentId}
                    data-test-id={props?.carousel?.id}
                >
                    <SectionHeaderWithPagination
                        title={__(props?.carousel?.title_resource_key ?? 'streaming_now')}
                        className={`${paginationClass} paging`}
                        seeAllUrl={f.getComponentSeeAllLink(props.meta, props.params, props.data_stream) as string}
                        seeAllText={__('home_carousel_see_all')}
                        onClick={() => handleSeeAllLinkClick(props.meta)}
                    />
                    <Carousel2 swiperSettings={settings} dir={language === 'ar' ? 'rtl' : 'ltr'}>
                        {enhancedFilteredContent.map((item, index) => {
                            const element = item.listings[0];
                            const img = element?.images?.length > 0 && element?.images[0]?.imageUrl;
                            const isFuture = item?.percentage <= 0;
                            const title = language === 'ar' ? element.localizedTitle.ar : element.title;
                            let isLocked = false;
                            if (item?.subscriptions?.length > 0) {
                                const subscriptionGuids = item?.subscriptions
                                    ? f2.pluck(item?.subscriptions, 'subscriptionGUID')
                                    : [];

                                isLocked = checkShowLockIcon(subscriptionGuids ?? [], item);
                            }

                            const program = (
                                <Program
                                    program={
                                        {
                                            badge: getProgramBadge(item?.percentage),
                                            channelImage: channelImageId.toLowerCase().includes('svg')
                                                ? item.channelImages?.find((img) => img.id === channelImageId)?.url ||
                                                  item.channelImages?.[0]?.url ||
                                                  ''
                                                : h.getImagizerUrl(
                                                      item.channelImages?.find((img) => img.id === channelImageId)
                                                          ?.url ||
                                                          item.channelImages?.[0]?.url ||
                                                          '',
                                                      176
                                                  ),
                                            channelTitle: item.channelTitle,
                                            duration:
                                                moment(element?.startTime).format('hh:mm a') +
                                                ' - ' +
                                                moment(element?.endTime).format('hh:mm a'),
                                            poster: h.getImagizerUrl(img, 750),
                                            text: title,
                                        } as IProgram
                                    }
                                />
                            );

                            return (
                                <div
                                    key={item?.channelId ?? element?.listingId ?? `streaming-${index}`}
                                    id={item?.channelId}
                                    data-test-id={item.channelId}
                                >
                                    {isLocked && (
                                        <Link
                                            to="#"
                                            onClick={(event) => {
                                                const shouldAllowDefault = handleStreamingItemClick(event, item);
                                                if (shouldAllowDefault) {
                                                    // Allow default behavior - navigate to the appropriate URL
                                                    const playUrl = getPlayUrl(item?.percentage, item);
                                                    if (playUrl) {
                                                        window.location.href = playUrl;
                                                    }
                                                }
                                            }}
                                        >
                                            <LockIcon corner channel={item} />
                                            {program}
                                        </Link>
                                    )}
                                    {!isLocked && (
                                        <Link
                                            to="#"
                                            onClick={(event) => {
                                                const shouldAllowDefault = handleStreamingItemClick(event, item);
                                                if (shouldAllowDefault && !isFuture) {
                                                    // Allow default behavior - navigate to the appropriate URL
                                                    const playUrl = getPlayUrl(item?.percentage, item);
                                                    if (playUrl) {
                                                        window.location.href = playUrl;
                                                    }
                                                }
                                            }}
                                            style={isFuture ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                                        >
                                            {program}
                                        </Link>
                                    )}
                                </div>
                            );
                        })}
                    </Carousel2>
                </div>
            )}
        </>
    );
};
